import { useEffect, useRef } from 'react';
import { charFromKeyCode, normalizeQrToken } from '../utils/qrToken';

type UseQrScannerOptions = {
	enabled: boolean; // 스캐너 활성화 여부 (QR 모드 && 모달 미노출 && 창 포커스 시)
	onScan: (code: string) => void; // 스캔 완료(Enter) 콜백
	interKeyDelay?: number; // 키 입력 간 최대 허용 간격(ms). 초과 시 버퍼 초기화
};

/**
 * QR 리더 키보드 웨지(HID) 입력 리스너
 *
 * 대부분의 QR 리더는 스캔한 문자열을 키보드처럼 빠르게 입력하고
 * 마지막에 Enter(개행)를 전송한다. 이 훅은 전역 keydown을 버퍼링하여
 * Enter 시점에 누적된 문자열을 onScan 으로 전달한다.
 */
export const useQrScanner = ({ enabled, onScan, interKeyDelay = 50 }: UseQrScannerOptions) => {
	const bufferRef = useRef('');
	const lastKeyTimeRef = useRef(0);
	// onScan 최신값 유지 (리스너 재등록 방지)
	const onScanRef = useRef(onScan);
	onScanRef.current = onScan;

	useEffect(() => {
		if (!enabled) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			// 입력 요소에 포커스가 있으면 무시 (직접 검색 입력 등)
			const target = event.target as HTMLElement;
			const tagName = target?.tagName;
			if (tagName === 'INPUT' || tagName === 'TEXTAREA' || target?.isContentEditable) {
				return;
			}

			const now = Date.now();

			// 사람 타이핑처럼 느린 입력이면 버퍼 초기화 (리더는 매우 빠름)
			if (now - lastKeyTimeRef.current > interKeyDelay) {
				bufferRef.current = '';
			}
			lastKeyTimeRef.current = now;

			if (event.key === 'Enter') {
				// 한글 IME 상태로 입력된 토큰은 영문 소문자로 변환해서 전달
				const code = normalizeQrToken(bufferRef.current);
				bufferRef.current = '';
				if (code.length > 0) {
					onScanRef.current(code);
				}
				return;
			}

			// 단일 문자만 누적 (한글로 들어온 문자는 Enter 시점에 영문으로 변환)
			if (event.key.length === 1) {
				bufferRef.current += event.key;
				return;
			}

			// 한글 IME 조합 중이면 event.key 가 'Process'/'Unidentified' 라 물리 키로 복원
			if (event.key === 'Process' || event.key === 'Unidentified') {
				bufferRef.current += charFromKeyCode(event.code);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [enabled, interKeyDelay]);
};
